import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "@wilco/api";
import { AppIcon, Button, IconArrowLeft, Notice, TextField } from "@wilco/ui";
import { useEffect, useRef, useState, type SubmitEvent } from "react";
import { t } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import { kioskApi, type KioskEmployee, type KioskMark } from "./api.ts";
import { localTime } from "./time.ts";

/*
 * «Кіоск» у входа (ATT-01, КД-03): полноэкранная страница планшета без
 * входа пользователя. Токен устройства выдают в «Відвідування →
 * Налаштування» один раз, планшет хранит его в localStorage. Отметка
 * бейджем (считыватель-клавиатура набирает номер и Enter) или выбором
 * себя и PIN. Всё крупно: отмечаются на ходу, в куртке и перчатках.
 */
const TOKEN_KEY = "wilco.kiosk.token";
// Итог отметки виден столько, сколько человек идёт мимо планшета.
const RESULT_MS = 5_000;

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Без хранилища (приватный режим) киоск работает до перезагрузки.
  }
}

const unauthorized = (e: unknown) => e instanceof ApiError && e.status === 401;

// Отказ сервера текстом по коду правила; без ответа сервера это обрыв связи.
const errorText = (e: unknown): string => (e instanceof ApiError ? errorNotice(e).title : t("kiosk.offline"));

type Screen = { kind: "idle" } | { kind: "pick" } | { kind: "pin"; employee: KioskEmployee } | { kind: "done"; mark: KioskMark } | { kind: "error"; text: string };

export function KioskPage() {
  const [token, setToken] = useState<string | null>(readToken);
  const [lost, setLost] = useState(false);
  if (!token) {
    return (
      <KioskSetup
        lost={lost}
        onToken={(v) => {
          writeToken(v);
          setLost(false);
          setToken(v);
        }}
      />
    );
  }
  return (
    <KioskMain
      token={token}
      onUnauthorized={() => {
        writeToken(null);
        setLost(true);
        setToken(null);
      }}
    />
  );
}

/** Подключение планшета: токен проверяется запросом списка сотрудников. */
function KioskSetup({ lost, onToken }: { lost: boolean; onToken: (token: string) => void }) {
  const [value, setValue] = useState("");
  const check = useMutation({ mutationFn: (v: string) => kioskApi.employees(v) });
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const v = value.trim();
    check.mutate(v, { onSuccess: () => onToken(v) });
  };
  return (
    <main className="kiosk kiosk-setup">
      <AppIcon name="attendance" size={64} />
      <h1 className="kiosk-title">{t("kiosk.setupTitle")}</h1>
      {lost ? <Notice tone="warning" title={t("kiosk.lost")} /> : null}
      <p className="kiosk-lead">{t("kiosk.setupLead")}</p>
      <form className="kiosk-setup-form" onSubmit={submit}>
        <TextField label={t("kiosk.token")} value={value} onChange={(e) => setValue(e.target.value)} autoComplete="off" spellCheck={false} />
        <Button type="submit" variant="primary" size="lg" disabled={!value.trim() || check.isPending}>
          {t("kiosk.connect")}
        </Button>
      </form>
      {check.error ? <Notice tone="danger" title={errorText(check.error)} /> : null}
    </main>
  );
}

function useClock(): string {
  const [now, setNow] = useState(() => new Date().toISOString());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date().toISOString()), 10_000);
    return () => clearInterval(id);
  }, []);
  return localTime(now);
}

function KioskMain({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [screen, setScreen] = useState<Screen>({ kind: "idle" });
  const clock = useClock();
  const employees = useQuery({ queryKey: ["kiosk", "employees", token], queryFn: () => kioskApi.employees(token), refetchInterval: 5 * 60_000, retry: false });
  const failed = (e: unknown) => (unauthorized(e) ? onUnauthorized() : setScreen({ kind: "error", text: errorText(e) }));
  const badge = useMutation({ mutationFn: (v: string) => kioskApi.badge(token, v), onSuccess: (mark) => setScreen({ kind: "done", mark }), onError: failed });
  const pin = useMutation({
    mutationFn: ({ employee, value }: { employee: KioskEmployee; value: string }) => kioskApi.pin(token, employee.id, value),
    onSuccess: (mark) => setScreen({ kind: "done", mark }),
    onError: failed,
  });

  useEffect(() => {
    if (employees.error && unauthorized(employees.error)) onUnauthorized();
  }, [employees.error, onUnauthorized]);

  // Итог и ошибка сами уходят: следующий человек видит пустой экран.
  useEffect(() => {
    if (screen.kind !== "done" && screen.kind !== "error") return;
    const id = setTimeout(() => setScreen({ kind: "idle" }), RESULT_MS);
    return () => clearTimeout(id);
  }, [screen]);

  return (
    <main className="kiosk" data-screen={screen.kind}>
      <header className="kiosk-head">
        <span className="kiosk-clock w-num" aria-label={t("kiosk.clock")}>
          {clock}
        </span>
        <span className="kiosk-brand">{t("kiosk.title")}</span>
      </header>
      {screen.kind === "idle" ? <Idle onBadge={(v) => badge.mutate(v)} onPin={() => setScreen({ kind: "pick" })} busy={badge.isPending} /> : null}
      {screen.kind === "pick" ? (
        <Pick employees={employees.data ?? []} onPick={(employee) => setScreen({ kind: "pin", employee })} onBack={() => setScreen({ kind: "idle" })} />
      ) : null}
      {screen.kind === "pin" ? (
        <PinPad employee={screen.employee} busy={pin.isPending} onSubmit={(value) => pin.mutate({ employee: screen.employee, value })} onBack={() => setScreen({ kind: "pick" })} />
      ) : null}
      {screen.kind === "done" ? <Done mark={screen.mark} onNext={() => setScreen({ kind: "idle" })} /> : null}
      {screen.kind === "error" ? (
        <section className="kiosk-result" data-tone="danger" role="alert">
          <p className="kiosk-result-main">{screen.text}</p>
          <Button variant="secondary" size="lg" onClick={() => setScreen({ kind: "idle" })}>
            {t("kiosk.again")}
          </Button>
        </section>
      ) : null}
    </main>
  );
}

/** Ожидание: поле бейджа в фокусе, считыватель набирает номер и Enter. */
function Idle({ onBadge, onPin, busy }: { onBadge: (v: string) => void; onPin: () => void; busy: boolean }) {
  const [value, setValue] = useState("");
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);
  return (
    <section className="kiosk-idle">
      <h1 className="kiosk-title">{t("kiosk.prompt")}</h1>
      <form
        className="kiosk-badge"
        onSubmit={(e) => {
          e.preventDefault();
          const v = value.trim();
          setValue("");
          if (v) onBadge(v);
        }}
      >
        <TextField
          ref={field}
          label={t("kiosk.badge")}
          variant="scan"
          inputMode="numeric"
          autoComplete="off"
          enterKeyHint="go"
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          data-scan-field=""
        />
      </form>
      <Button variant="secondary" size="lg" block className="kiosk-big" onClick={onPin}>
        {t("kiosk.withPin")}
      </Button>
    </section>
  );
}

function Pick({ employees, onPick, onBack }: { employees: KioskEmployee[]; onPick: (e: KioskEmployee) => void; onBack: () => void }) {
  return (
    <section className="kiosk-pick">
      <div className="kiosk-row">
        <Button variant="ghost" size="lg" onClick={onBack}>
          <IconArrowLeft />
          {t("kiosk.back")}
        </Button>
        <h1 className="kiosk-title">{t("kiosk.pickTitle")}</h1>
      </div>
      <ul className="kiosk-names">
        {employees.map((e) => (
          <li key={e.id}>
            <button type="button" className="kiosk-name" onClick={() => onPick(e)}>
              {e.name}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"] as const;

/** Клавиатура PIN крупными кнопками: системная на планшете перекрывает пол-экрана. */
function PinPad({ employee, busy, onSubmit, onBack }: { employee: KioskEmployee; busy: boolean; onSubmit: (pin: string) => void; onBack: () => void }) {
  const [pin, setPin] = useState("");
  const press = (k: string) => {
    if (k === "⌫") setPin((p) => p.slice(0, -1));
    else if (k) setPin((p) => (p.length < 6 ? p + k : p));
  };
  return (
    <section className="kiosk-pin">
      <div className="kiosk-row">
        <Button variant="ghost" size="lg" onClick={onBack}>
          <IconArrowLeft />
          {t("kiosk.back")}
        </Button>
        <h1 className="kiosk-title">{employee.name}</h1>
      </div>
      <p className="kiosk-dots" aria-label={t("kiosk.pinEntered", { n: pin.length })}>
        {pin.length === 0 ? t("kiosk.pinPrompt") : "●".repeat(pin.length)}
      </p>
      <div className="kiosk-keys" role="group" aria-label={t("kiosk.keypad")}>
        {KEYS.map((k, i) =>
          k ? (
            <button key={i} type="button" className="kiosk-key w-num" aria-label={k === "⌫" ? t("kiosk.erase") : k} onClick={() => press(k)}>
              {k}
            </button>
          ) : (
            <span key={i} />
          ),
        )}
      </div>
      <Button variant="primary" size="lg" block className="kiosk-big" disabled={pin.length < 4 || busy} onClick={() => onSubmit(pin)}>
        {t("kiosk.mark")}
      </Button>
    </section>
  );
}

function Done({ mark, onNext }: { mark: KioskMark; onNext: () => void }) {
  const inbound = mark.action === "checked_in";
  return (
    <section className="kiosk-result" data-tone={inbound ? "success" : "info"} role="status">
      <p className="kiosk-result-name">{mark.name}</p>
      <p className="kiosk-result-main">{inbound ? t("kiosk.in", { time: localTime(mark.at) }) : t("kiosk.out", { time: localTime(mark.at) })}</p>
      <Button variant="secondary" size="lg" onClick={onNext}>
        {t("kiosk.next")}
      </Button>
    </section>
  );
}
